// /src/firebase/messagingUtils.js

import { db } from '../firebaseConfig'; // assumes your firebase init is here
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { toast } from 'react-toastify';

export const showConfirmToast = (message, onConfirm) => {
  const id = toast(
    ({ closeToast }) => (
      <div>
        <p style={{ marginBottom: '8px' }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            style={{ backgroundColor: '#ccc', border: 'none', padding: '6px 12px', borderRadius: '4px' }}
            onClick={() => toast.dismiss(id)}
          >
            Cancel
          </button>
          <button
            style={{ backgroundColor: '#d9534f', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px' }}
            onClick={() => {
              toast.dismiss(id);
              onConfirm();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    }
  );
};

export const getUsersFromFirestore = async () => {
  const snapshot = await getDocs(collection(db, 'users'));
  return snapshot.docs.map(docSnap => {
    const data = docSnap.data();

    return {
      id: docSnap.id,
      fullName: data.firstInitialLastName || `${data.firstName?.[0] || '?'}${data.lastName ? '.' + data.lastName : ''}`,
      agency: data.agncy || data.agency || 'Unknown',
      dutyStatus: data.dutyStatus || data.DutyStatus || 'off-duty',
      messagePreference: data.notificationPrefs?.message || 'normal', // customize as needed
      ...data
    };
  });
};

export const getGroupsForUser = async () => {
  const snapshot = await getDocs(collection(db, 'groups'));
  return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
};

export const createGroupInFirestore = async (group) => {
  const docRef = await addDoc(collection(db, 'conversations'), group); // correct collection
  return docRef.id;
};

export const createUserList = async (currentUserId, listName) => {
  try {
    const userListsRef = collection(db, 'users', currentUserId, 'userLists');
    const docRef = await addDoc(userListsRef, {
      name: listName,
      createdAt: serverTimestamp(),
    });
    return { id: docRef.id, name: listName };
  } catch (error) {
    console.error('Error creating list:', error);
    throw error;
  }
};

export const addUserToList = async (currentUserId, listId, otherUserId) => {
  try {
    const memberRef = doc(
      db,
      'users',
      currentUserId,
      'userLists',
      listId,
      'members',
      otherUserId
    );
    await setDoc(memberRef, {
      userId: otherUserId,
      addedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding user to list:', error);
    throw error;
  }
};

export const getUserLists = async (currentUserId) => {
  try {
    const listsRef = collection(db, 'users', currentUserId, 'userLists');
    const q = query(listsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    console.error('Error fetching lists:', error);
    return [];
  }
};

export const getListMembers = async (currentUserId, listId) => {
  try {
    const snapshot = await getDocs(
      collection(db, 'users', currentUserId, 'userLists', listId, 'members')
    );
    return snapshot.docs.map((docSnap) => docSnap.id);
  } catch (error) {
    console.error('Error fetching list members:', error);
    return [];
  }
};

export const sendMessageToConversation = async (conversationId, message) => {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  if (!conversationSnap.exists() || conversationSnap.data()?.deleting === true) {
    throw new Error('This conversation is being deleted.');
  }

  const messageWithTimestamp = {
    ...message,
    createdAt: serverTimestamp(),
  };

  await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageWithTimestamp);

  await updateDoc(conversationRef, {
    lastMessageAt: serverTimestamp(),
    lastMessageSender: message.senderId,
  });
};

export const markConversationAsViewed = async (conversationId, userId) => {
  const viewRef = doc(db, 'conversations', conversationId, 'views', userId);
  await setDoc(viewRef, { lastViewed: new Date() }, { merge: true });
};

const buildDirectConversationIds = (userId1, userId2) => {
  const ordered = [userId1, userId2].sort();
  const dmKey = `${ordered[0]}_${ordered[1]}`;

  return {
    ordered,
    dmKey,
    canonicalId: `dm_${dmKey}`,
    legacyId: `direct_${dmKey}`,
  };
};

const readConversationIfAccessible = async (conversationId) => {
  try {
    const snap = await getDoc(doc(db, 'conversations', conversationId));
    if (!snap.exists()) {
      return null;
    }

    return { id: conversationId, ...snap.data() };
  } catch (error) {
    if (error?.code === 'permission-denied') {
      return null;
    }
    throw error;
  }
};

export const getExistingDirectConversationId = async (userId1, userId2) => {
  if (!userId1 || !userId2 || userId1 === userId2) {
    return null;
  }

  const { canonicalId, legacyId, ordered } = buildDirectConversationIds(userId1, userId2);

  for (const candidateId of [canonicalId, legacyId]) {
    const conversation = await readConversationIfAccessible(candidateId);
    if (!conversation || conversation.isGroup === true) {
      continue;
    }

    const members = Array.isArray(conversation.members) ? [...conversation.members].sort() : [];
    if (members.length === 2 && members[0] === ordered[0] && members[1] === ordered[1]) {
      return conversation.id;
    }
  }

  return null;
};

export const getOrCreateDirectConversation = async (userId1, userId2, options = {}) => {
  const existingId = await getExistingDirectConversationId(userId1, userId2);
  if (existingId) {
    console.log('[DM] Reusing existing direct conversation:', existingId);
    return existingId;
  }

  const { ordered, dmKey, canonicalId } = buildDirectConversationIds(userId1, userId2);
  const conversationRef = doc(db, 'conversations', canonicalId);

  const payload = {
    type: 'direct',
    dmKey,
    members: ordered,
    isGroup: false,
    updatedAt: serverTimestamp(),
  };

  if (options.memberProfiles) {
    payload.memberProfiles = options.memberProfiles;
  }

  await setDoc(conversationRef, {
    createdAt: serverTimestamp(),
    lastMessageAt: null,
    ...payload,
  }, { merge: true });

  console.log('[DM] Ensured direct conversation:', canonicalId);
  return canonicalId;
};
